/**
 * Deep-link parser for the Docs_Slider.
 *
 * Reads `?docs=…` from the search string and `#anchor` from the hash so the
 * Docs_Slider can auto-open at a specific document on mount. Called by
 * {@link ../help-docs-panel/DocsSliderProvider.tsx | DocsSliderProvider} with
 * `window.location.search` and `window.location.hash` verbatim.
 *
 * This module is a leaf utility. It has no side effects, never throws, and
 * imports nothing from the rest of `help-docs-panel/`.
 *
 * @see .kiro/specs/help-docs-panel/requirements.md#requirement-1-docs-slider (1.7, 1.8)
 * @see .kiro/specs/help-docs-panel/design.md — Function F2 companion parser
 */

/**
 * Successful parse result. Either field MAY be absent.
 *
 * @remarks
 * - `docPath` — always relative to `/docs`, always ends in `.md`, never
 *   contains `..` and never starts with `/`.
 * - `anchor` — the heading slug (no leading `#`), always matches
 *   `/^[a-z0-9-]+$/`.
 * - `anchor` is NEVER present without `docPath`. If the query param is
 *   missing or invalid, both fields are dropped and `{}` is returned.
 */
export interface DeepLinkResult {
  readonly docPath?: string;
  readonly anchor?: string;
}

/** Slug pattern accepted for anchors (Req 5.7 heading slugs, Req 1.8). */
const ANCHOR_PATTERN = /^[a-z0-9-]+$/;

/**
 * Validate a decoded `docs=` value.
 *
 * Enforces the deep-link path safety rules from Req 1.7:
 * - MUST end in `.md`
 * - MUST NOT start with `/` (absolute path)
 * - MUST NOT contain `..` (traversal)
 */
function isValidDocPath(path: string): boolean {
  if (path.length === 0) return false;
  if (!path.endsWith(".md")) return false;
  if (path.startsWith("/")) return false;
  if (path.includes("..")) return false;
  return true;
}

/**
 * Safely `decodeURIComponent` a value; returns `null` on malformed sequences.
 *
 * `URLSearchParams` and `window.location.hash` are both lenient with
 * malformed percent-encoding (they leave raw `%XX` in the result rather than
 * throwing). Re-running `decodeURIComponent` catches those and lets callers
 * drop the offending param per Req 1.7.
 */
function safeDecode(value: string): string | null {
  try {
    return decodeURIComponent(value);
  } catch {
    return null;
  }
}

/**
 * Parse a deep-link from `window.location.search` and `window.location.hash`.
 *
 * Never throws. See {@link DeepLinkResult} for the shape of a successful
 * parse. When the deep link is missing or invalid, returns `{}`.
 *
 * @param search — the raw search string, e.g. `"?docs=api/statements.md"`.
 *   Leading `?` is tolerated and optional (matches `window.location.search`
 *   which always includes it when non-empty).
 * @param hash — the raw hash string, e.g. `"#collection-rate"`. Leading `#`
 *   is tolerated and optional (matches `window.location.hash`).
 *
 * @remarks
 * Validation rules applied in order:
 * 1. If `?docs=` is missing or its decoded value fails
 *    {@link isValidDocPath}, return `{}` (the anchor is dropped too — a
 *    deep-link without a docPath is meaningless).
 * 2. If the anchor decode succeeds and matches `/^[a-z0-9-]+$/`, include it.
 *    Otherwise drop the anchor but keep the (valid) docPath.
 * 3. When `?docs=` appears multiple times, the first value wins
 *    (`URLSearchParams.get` semantics).
 *
 * @see .kiro/specs/help-docs-panel/requirements.md#requirement-1-docs-slider (1.7, 1.8)
 */
export function parseDeepLink(search: string, hash: string): DeepLinkResult {
  // 1) Extract and validate docPath. Any failure returns `{}`.
  let docPath: string | undefined;
  const params = new URLSearchParams(search);
  const rawDocs = params.get("docs");
  if (rawDocs !== null) {
    const decoded = safeDecode(rawDocs);
    if (decoded !== null && isValidDocPath(decoded)) {
      docPath = decoded;
    }
  }
  if (docPath === undefined) return {};

  // 2) Extract and validate anchor. Failure drops the anchor only.
  let anchor: string | undefined;
  const rawHash = hash.startsWith("#") ? hash.slice(1) : hash;
  if (rawHash.length > 0) {
    const decodedHash = safeDecode(rawHash);
    if (decodedHash !== null && ANCHOR_PATTERN.test(decodedHash)) {
      anchor = decodedHash;
    }
  }

  return anchor === undefined ? { docPath } : { docPath, anchor };
}
